import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, Clock, CheckCircle, XCircle, Workflow, DollarSign, TrendingUp, Truck, Shield, FileText, Package, Globe } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_DASHBOARD_COPY } from "@/pages/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="bg-[#09120D] border border-[#1A2E22] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[#A1B2A8] text-xs">{label}</span>
      </div>
      <span className="font-['Outfit'] text-2xl font-bold text-white">{value}</span>
      {sub && <p className="text-[#A1B2A8]/50 text-xs mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminOverview() {
  const { user } = useAuth();
  const role = (user?.via_role || "").toLowerCase();
  const copy = ROLE_DASHBOARD_COPY[role] || { title: "Dashboard", sub: "Operating Infrastructure for Global Food Trade" };
  const [appStats, setAppStats] = useState({});
  const [txnStats, setTxnStats] = useState({});
  const [econ, setEcon] = useState({});

  const fetchStats = useCallback(async () => {
    try {
      const [a, t, e] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/transaction-stats`, { withCredentials: true }),
        axios.get(`${API}/admin/platform-economics`, { withCredentials: true }),
      ]);
      setAppStats(a.data); setTxnStats(t.data); setEcon(e.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  const fmtVol = (v) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`;

  return (
    <div data-testid="admin-overview">
      <h1 className="font-['Outfit'] text-2xl font-bold text-white mb-1" data-testid="dashboard-title">{copy.title}</h1>
      <p className="text-[#A1B2A8]/60 text-sm mb-6" data-testid="dashboard-subtitle">{copy.sub}</p>

      <h2 className="text-[#A1B2A8] text-xs font-medium uppercase tracking-wider mb-3">Platform Revenue & Returns Lifecycle</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="platform-revenue-kpis">
        <StatCard icon={DollarSign} label="Capital Deployed" value={fmtVol(econ.total_capital_deployed || 0)} color="text-white" sub={`${econ.total_flows || 0} flows`} />
        <StatCard icon={TrendingUp} label="Revenue Realized" value={fmtVol(econ.total_revenue_realized || 0)} color="text-[#22C55E]" sub={`${econ.completed_flows || 0} completed`} />
        <StatCard icon={TrendingUp} label="Revenue Pipeline" value={fmtVol(econ.total_revenue_pipeline || 0)} color="text-yellow-400" sub="Across all flows" />
        <StatCard icon={DollarSign} label="Buyer Savings" value={fmtVol(econ.total_buyer_savings || 0)} color="text-blue-400" sub="vs traditional" />
        <StatCard icon={Shield} label="Avg Fee" value={`${econ.avg_fee_pct || 0}%`} color="text-purple-400" sub="Platform margin" />
        <StatCard icon={Clock} label="Days to Close" value={`${econ.avg_days_to_close || 0}d`} color="text-cyan-400" sub="Avg lifecycle" />
      </div>

      <h2 className="text-[#A1B2A8] text-xs font-medium uppercase tracking-wider mb-3">Network</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <StatCard icon={Globe} label="Exporters" value={appStats.suppliers || 0} color="text-[#22C55E]" />
        <StatCard icon={Users} label="Buyers" value={appStats.buyers || 0} color="text-blue-400" />
        <StatCard icon={Clock} label="Pending" value={appStats.pending || 0} color="text-yellow-400" />
        <StatCard icon={CheckCircle} label="Verified" value={appStats.approved || 0} color="text-[#22C55E]" />
        <StatCard icon={XCircle} label="Rejected" value={appStats.rejected || 0} color="text-red-400" />
        <StatCard icon={Users} label="Total Network" value={appStats.total || 0} color="text-white" />
      </div>

      <h2 className="text-[#A1B2A8] text-xs font-medium uppercase tracking-wider mb-3">Flow Engine</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <StatCard icon={Workflow} label="Active Flows" value={txnStats.total || 0} color="text-white" />
        <StatCard icon={DollarSign} label="Trade Value" value={fmtVol(txnStats.total_volume || 0)} color="text-[#22C55E]" />
        <StatCard icon={Package} label="Shipments" value={txnStats.in_progress || 0} color="text-blue-400" sub="In execution" />
        <StatCard icon={Clock} label="Pending" value={txnStats.pending || 0} color="text-yellow-400" sub="Awaiting settlement" />
        <StatCard icon={CheckCircle} label="Settled" value={txnStats.completed || 0} color="text-[#22C55E]" sub="Released" />
        <StatCard icon={Shield} label="Trust" value="96%" color="text-[#22C55E]" sub="On-time delivery" />
      </div>

      <h2 className="text-[#A1B2A8] text-xs font-medium uppercase tracking-wider mb-3">AI Confidence Engine</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#09120D] border border-[#1A2E22] p-5">
          <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-[#22C55E]" /><span className="text-[#A1B2A8] text-xs">Commercial Intelligence</span></div>
          <p className="text-white font-['Outfit'] font-semibold text-sm">Active — AI-powered confidence scoring</p>
          <p className="text-[#A1B2A8]/50 text-xs mt-1">Counterparty intelligence, risk assessment, trade insights</p>
        </div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-5">
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-blue-400" /><span className="text-[#A1B2A8] text-xs">Execution Engine</span></div>
          <p className="text-white font-['Outfit'] font-semibold text-sm">Active — Milestone-based flow control</p>
          <p className="text-[#A1B2A8]/50 text-xs mt-1">Live tracking, settlement triggers, document verification</p>
        </div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-5">
          <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-purple-400" /><span className="text-[#A1B2A8] text-xs">Trust & Compliance</span></div>
          <p className="text-white font-['Outfit'] font-semibold text-sm">Active — Verified participants & documents</p>
          <p className="text-[#A1B2A8]/50 text-xs mt-1">KYC, commercial profiles, compliance screening</p>
        </div>
      </div>
    </div>
  );
}
